import React, { useEffect } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import ArticleIcon from '@mui/icons-material/Article';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import ClockIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { supabase } from '../../lib/supabase';
import { motion } from 'framer-motion';

const LandingPage = () => {
  const navigate = useNavigate();
  const { subscription } = useSubscription();
  const isAuthenticated = Boolean(subscription);

  // Enhanced SEO metadata and schema markup
  useEffect(() => {
    // Update meta tags
    document.title = "PageCrafter AI | Best AI Content Generator & Automated Article Writer 2024";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'Transform your content creation with PageCrafter AI. Our AI-powered platform generates SEO-optimized articles, blog posts, and web content in minutes. The most advanced automated article writer for digital marketers and content creators.'
      );
    }

    // Add schema markup
    const schemaScript = document.createElement('script');
    schemaScript.type = "application/ld+json";
    const schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "PageCrafter AI",
      "applicationCategory": "Content Generation Tool",
      "description": "AI-powered content generator and automated article writer for SEO-optimized web content",
      "operatingSystem": "Web-based",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "GBP",
        "description": "Free plan available with premium features"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "1000"
      },
      "featureList": [
        "AI Content Generation",
        "SEO Optimization",
        "Smart Content Management",
        "Automated Article Writing",
        "Content Templates"
      ],
      "mainEntity": [{
        "@type": "Question",
        "name": "How does the AI content generator work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our AI content generator uses advanced natural language processing to create original, SEO-optimized content from your input. Simply provide your topic and key points, and our AI will generate a complete article in minutes."
        }
      }, {
        "@type": "Question",
        "name": "Is the content unique and plagiarism-free?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, all content generated by PageCrafter AI is 100% original and plagiarism-free. Our AI creates unique content based on your specific inputs and requirements."
        }
      }]
    };
    schemaScript.text = JSON.stringify(schema);
    document.head.appendChild(schemaScript);

    // Cleanup
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  const handleUpgrade = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'Accept': 'application/json'
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create checkout session');
      }

      const { url } = await response.json();
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Failed to start upgrade process. Please try again.');
    }
  };

  const renderFreePlanButton = () => {
    if (!isAuthenticated) {
      return (
        <Button
          onClick={() => navigate('/login')}
          variant="contained"
          color="primary"
          size="large"
          className="w-full mt-4"
        >
          Get Started
        </Button>
      );
    }
    if (subscription?.plan_type === 'pro') {
      return (
        <span className="mt-4 text-gray-900 font-semibold">Current Plan</span>
      );
    }
    return (
      <Button
        onClick={() => navigate('/generate')}
        variant="contained"
        color="primary"
        size="large"
        className="w-full mt-4"
      >
        Start Generating
      </Button>
    );
  };

  const renderProPlanButton = () => {
    if (!isAuthenticated) {
      return (
        <Button
          onClick={() => navigate('/login')}
          variant="contained"
          color="primary"
          size="large"
          className="w-full"
        >
          Get Pro Access
        </Button>
      );
    }
    if (subscription?.plan_type === 'pro') {
      return (
        <span className="mt-4 text-gray-900 font-semibold">Current Plan</span>
      );
    }
    return (
      <Button
        onClick={handleUpgrade}
        variant="contained"
        color="primary"
        size="large"
        className="w-full"
      >
        Upgrade to Pro
      </Button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-blue-50">
      {/* Enhanced Hero Section */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 right-1/4 w-72 h-72 bg-cyan-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-1/3 w-72 h-72 bg-pink-100 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-6xl sm:text-7xl font-extrabold tracking-tight mb-6 leading-tight"
          >
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-3xl text-gray-800 mb-4 font-bold">
              Create SEO-Optimized Content in Minutes
            </h2>
            <p className="max-w-3xl mx-auto text-lg text-gray-600 mb-8">
              Transform your content creation workflow with our AI writing assistant. 
              Generate blog posts, articles, and web content that ranks on Google.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12">
              <Button
                onClick={() => navigate('/generate')}
                variant="contained"
                size="large"
                className="group relative px-8 py-3 text-lg"
                sx={{
                  background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
                  borderRadius: '12px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 6px rgba(25, 118, 210, 0.12)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #42a5f5)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 6px 8px rgba(25, 118, 210, 0.2)',
                  },
                }}
              >
                Try AI Content Generator Free
                <motion.span
                  className="absolute right-4 top-1/2 transform -translate-y-1/2"
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  →
                </motion.span>
              </Button>

              <Button
                onClick={() => navigate('/login')}
                variant="outlined"
                size="large"
                className="px-8 py-3 text-lg"
                sx={{
                  borderRadius: '12px',
                  borderWidth: '2px',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    borderWidth: '2px',
                    background: 'rgba(25, 118, 210, 0.04)',
                  },
                }}
              >
                See How It Works
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
              {[
                { title: '100% Original', subtitle: 'Plagiarism-Free Content' },
                { title: 'SEO Ready', subtitle: 'Optimized for Search' },
                { title: '5-Min Setup', subtitle: 'Quick & Easy to Use' }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 + (index * 0.1) }}
                  className="p-4 bg-white/50 backdrop-blur-sm rounded-xl border border-blue-100"
                >
                  <p className="font-bold text-blue-600">{item.title}</p>
                  <p className="text-sm text-gray-600">{item.subtitle}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </header>

      {/* Add to your tailwind.config.js */}
      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* New Use Cases Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            AI Content Creation for Every Need
          </h2>
          <div className="grid md:grid-cols-4 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Blog Posts</h3>
              <p className="text-gray-600">Generate engaging blog content optimized for search engines</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Product Descriptions</h3>
              <p className="text-gray-600">Create compelling product content that converts</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">SEO Articles</h3>
              <p className="text-gray-600">Write keyword-optimized articles that rank</p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <h3 className="text-xl font-semibold mb-2">Web Copy</h3>
              <p className="text-gray-600">Generate professional website content</p>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section className="py-16 bg-gray-50" aria-label="Key Features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Powerful AI Content Creation Tools
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <ArticleIcon className="text-blue-600" fontSize="large" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                Automated Article Writer
              </h3>
              <p className="text-gray-600 text-center">
                Generate complete, SEO-optimized articles in minutes with our advanced AI technology. Perfect for blogs, websites, and marketing content.
              </p>
            </div>

            {/* Additional enhanced feature boxes */}
            <div className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <SearchIcon className="text-blue-600" fontSize="large" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                Built-in SEO Optimization
              </h3>
              <p className="text-gray-600 text-center">
                Every piece of content is structured for maximum search engine visibility with optimized meta tags, headers, and keyword placement.
              </p>
            </div>

            <div className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                <EditIcon className="text-blue-600" fontSize="large" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 text-center">
                Smart Content Management
              </h3>
              <p className="text-gray-600 text-center">
                Save, edit, and refine your AI-generated content with our intuitive editor. Download in multiple formats for immediate use.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Value Metrics Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-12">
            Why Choose PageCrafter AI?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4 text-blue-600">
                <ClockIcon fontSize="large" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">
                5 Minute Setup
              </h4>
              <p className="text-gray-600">
                From idea to complete article in under 5 minutes. No writing experience needed.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4 text-blue-600">
                <SearchIcon fontSize="large" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">
                Built-in SEO
              </h4>
              <p className="text-gray-600">
                Automatic keyword optimization and meta descriptions for better search rankings.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow hover:shadow-lg transition-shadow duration-300">
              <div className="mb-4 text-blue-600">
                <EditIcon fontSize="large" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">
                Full Control
              </h4>
              <p className="text-gray-600">
                Edit and customize your content directly in our built-in editor before downloading.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-50 rounded-full mix-blend-multiply filter blur-xl opacity-75 animate-blob"></div>
          <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-cyan-50 rounded-full mix-blend-multiply filter blur-xl opacity-75 animate-blob animation-delay-2000"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-16">
              Choose Your Plan
            </h2>

            <div className="grid md:grid-cols-2 gap-8 items-stretch">
              {/* Free Plan Card */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="relative group"
              >
                <div className="relative bg-white rounded-2xl p-8 shadow-lg transition-all duration-300 hover:shadow-xl border border-gray-100 h-full flex flex-col">
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                      Free Plan
                    </h3>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">£0</span>
                    </div>
                    <ul className="space-y-4 mb-8 text-left">
                      {['3 Articles per month', 'Basic templates', 'Standard support'].map((feature, index) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <CheckIcon className="text-blue-500 mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    onClick={() => navigate('/generate')}
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      py: 2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
                      boxShadow: '0 4px 6px rgba(25, 118, 210, 0.12)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #1565c0, #42a5f5)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 8px rgba(25, 118, 210, 0.2)',
                      },
                    }}
                  >
                    Get Started
                  </Button>
                </div>
              </motion.div>

              {/* Pro Plan Card */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="relative group"
              >
                <div className="absolute -inset-[2px] bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl opacity-75 blur-sm group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative bg-white rounded-2xl p-8 h-full flex flex-col">
                  <div className="flex-grow">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">
                      Pro Plan
                    </h3>
                    <div className="mb-6">
                      <span className="text-5xl font-bold text-gray-900">£19</span>
                      <span className="text-xl text-gray-500 font-medium">/mo</span>
                    </div>
                    <ul className="space-y-4 mb-8 text-left">
                      {[
                        '50 Articles per month',
                        'All Premium Templates',
                        'Priority Support',
                        'Advanced Features'
                      ].map((feature, index) => (
                        <li key={index} className="flex items-center text-gray-600">
                          <CheckIcon className="text-blue-500 mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Button
                    onClick={handleUpgrade}
                    variant="contained"
                    fullWidth
                    size="large"
                    sx={{
                      py: 2,
                      borderRadius: '12px',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
                      boxShadow: '0 4px 6px rgba(25, 118, 210, 0.12)',
                      '&:hover': {
                        background: 'linear-gradient(45deg, #1565c0, #42a5f5)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 6px 8px rgba(25, 118, 210, 0.2)',
                      },
                    }}
                  >
                    Get Pro Access
                  </Button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
